import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { platformService } from '@/services/platformService'

const STAGES = ['PROSPECT', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST'] as const
const STAGE_LABEL: Record<string, string> = {
    PROSPECT: 'Prospecto', PROPOSAL: 'Propuesta', NEGOTIATION: 'Negociación', WON: 'Ganada', LOST: 'Perdida',
}
const clp = (n: number) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n || 0)
const EMPTY = { title: '', amountClp: '', stage: 'PROSPECT', expectedClose: '' }

export default function PipelinePage() {
    const [opps, setOpps] = useState<any[]>([])
    const [showModal, setShowModal] = useState(false)
    const [form, setForm] = useState<any>(EMPTY)

    const reload = () => { platformService.crmOpportunities().then(setOpps).catch(() => setOpps([])) }
    useEffect(reload, [])

    const create = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            await platformService.crmCreateOpp({ ...form, amountClp: Number(form.amountClp) || 0 })
            toast.success('Oportunidad creada'); setShowModal(false); setForm(EMPTY); reload()
        } catch { toast.error('No se pudo crear') }
    }
    const move = async (o: any, dir: number) => {
        const idx = STAGES.indexOf(o.stage)
        const next = STAGES[idx + dir]
        if (!next) return
        try { await platformService.crmUpdateOpp(o.id, { stage: next }); reload() } catch { toast.error('No se pudo mover') }
    }
    const remove = async (o: any) => {
        try { await platformService.crmDeleteOpp(o.id); reload() } catch { /* noop */ }
    }

    const inStage = (s: string) => opps.filter((o) => o.stage === s)
    const stageTotal = (s: string) => inStage(s).reduce((acc, o) => acc + (o.amountClp || 0), 0)

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Pipeline</h1>
                <button onClick={() => setShowModal(true)} className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">+ Nueva oportunidad</button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
                {STAGES.map((s) => (
                    <div key={s} className="rounded-xl border border-slate-800 bg-slate-900/40 p-3">
                        <div className="mb-2 flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-200">{STAGE_LABEL[s]}</span>
                            <span className="text-xs text-slate-500">{inStage(s).length}</span>
                        </div>
                        <div className="mb-3 text-xs text-slate-500">{clp(stageTotal(s))}</div>
                        <div className="space-y-2">
                            {inStage(s).map((o) => (
                                <div key={o.id} className="rounded-lg border border-slate-700 bg-slate-800 p-3">
                                    <div className="text-sm text-slate-100">{o.title}</div>
                                    <div className="mt-1 text-xs text-emerald-400">{clp(o.amountClp)}</div>
                                    {o.expectedClose && <div className="text-[11px] text-slate-500">cierre: {o.expectedClose}</div>}
                                    <div className="mt-2 flex items-center justify-between text-xs">
                                        <div className="flex gap-1">
                                            <button onClick={() => move(o, -1)} disabled={STAGES.indexOf(s) === 0} className="rounded bg-slate-700 px-1.5 hover:bg-slate-600 disabled:opacity-30">←</button>
                                            <button onClick={() => move(o, 1)} disabled={STAGES.indexOf(s) === STAGES.length - 1} className="rounded bg-slate-700 px-1.5 hover:bg-slate-600 disabled:opacity-30">→</button>
                                        </div>
                                        <button onClick={() => remove(o)} className="text-rose-400 hover:underline">×</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
                    <form onSubmit={create} className="w-full max-w-md space-y-3 rounded-xl border border-slate-700 bg-slate-800 p-6">
                        <h2 className="text-lg font-semibold text-slate-100">Nueva oportunidad</h2>
                        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título" required
                            className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500" />
                        <input type="number" value={form.amountClp} onChange={(e) => setForm({ ...form, amountClp: e.target.value })} placeholder="Monto CLP"
                            className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500" />
                        <input type="date" value={form.expectedClose} onChange={(e) => setForm({ ...form, expectedClose: e.target.value })}
                            className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500" />
                        <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                            className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500">
                            {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                        </select>
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowModal(false)} className="rounded-md border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700">Cancelar</button>
                            <button type="submit" className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">Crear</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    )
}
